import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../../components/Common/Footer/Footer';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import { Clock, ArrowLeft, CheckCircle2, XCircle, AlertCircle, Calendar, ChevronDown, ChevronUp, LayoutDashboard } from 'lucide-react';
import Pagination from '../../../components/Common/Pagination/Pagination';
import NoData from '../../../components/Common/NoData/NoData';
import CustomSelect from '../../../components/Common/Select/CustomSelect';
import styles from './MonthlyReportView.module.css';

const MonthlyReportView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [employeeData, setEmployeeData] = useState([]);
    const [employeeDetails, setEmployeeDetails] = useState(null);
    const [summaryData, setSummaryData] = useState(null);
    const [isSummaryOpen, setIsSummaryOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        total: 0,
        per_page: 10
    });
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    const monthOptions = [
        { value: 1, label: 'January' },
        { value: 2, label: 'February' },
        { value: 3, label: 'March' },
        { value: 4, label: 'April' },
        { value: 5, label: 'May' },
        { value: 6, label: 'June' },
        { value: 7, label: 'July' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'October' },
        { value: 11, label: 'November' },
        { value: 12, label: 'December' }
    ];

    const [yearOptions, setYearOptions] = useState([]);
    const [yearPage, setYearPage] = useState(1);
    const YEARS_PER_PAGE = 20;

    useEffect(() => {
        const currentYear = new Date().getFullYear();
        const options = Array.from({ length: yearPage * YEARS_PER_PAGE }, (_, i) => ({
            value: currentYear - i,
            label: (currentYear - i).toString()
        }));
        setYearOptions(options);
    }, [yearPage]);

    const handleYearScrollToBottom = () => {
        setYearPage(prev => prev + 1);
    };

    const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

    const fetchMonthlyReport = async () => {
        try {
            setIsLoading(true);
            const response = await api.get('/attendance/monthly-report', {
                params: { 
                    employee_id: id,
                    month: selectedMonth,
                    year: selectedYear,
                    page: pagination.current_page,
                    per_page: pagination.per_page
                }
            });
            if (response.data.success) {
                const { data, current_page, last_page, total, per_page } = response.data.data;
                setEmployeeData(data || []);
                setSummaryData(response.data.summary);
                if (data && data.length > 0 && data[0].employee) {
                    setEmployeeDetails(data[0].employee);
                } else if (!employeeDetails) {
                    // Fetch basic employee details if no attendance records
                    const empRes = await api.get(`/employees/${id}`);
                    if (empRes.data.success) {
                        setEmployeeDetails(empRes.data.data);
                    }
                }
                setPagination({ current_page, last_page, total, per_page });
            }
        } catch (error) {
            console.error('Error fetching monthly report:', error);
            toast.error('Failed to load monthly report');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchMonthlyReport();
        }
    }, [id, selectedMonth, selectedYear, pagination.current_page, pagination.per_page]);

    const formatTime = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatDuration = (minutes) => {
        if (!minutes) return '-';
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}h ${mins}m`;
    };

    const getStatusClass = (status) => {
        if (!status) return styles['status-pending'];
        const s = status.toLowerCase();
        if (s.includes('full_day') || s.includes('working')) return styles['status-present'];
        if (s.includes('absent')) return styles['status-absent'];
        if (s.includes('half')) return styles['status-half'];
        if (s.includes('leave')) return styles['status-leave'];
        return styles['status-pending'];
    };

    return (
        <div className={`${styles['dashboard-layout']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
            {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

            <div className={styles['content-wrapper']}>
                <Header onMenuClick={toggleSidebar} />

                <main className={styles['main-content']}>
                    <Breadcrumbs />

                    <div className={styles['page-header']}>
                        <div className={styles['header-title-section']}>
                            <div className={styles['title-with-back']}>
                                <button className={styles['back-btn']} onClick={() => navigate(-1)}>
                                    <ArrowLeft size={20} />
                                </button>
                                <h1>Monthly Report</h1>
                            </div>
                            <p>
                                Showing monthly attendance report for {employeeDetails ? `${employeeDetails.employee_name} (${employeeDetails.employee_code})` : 'employee'}.
                            </p>
                        </div>
                        <div className={styles['header-actions']} style={{ gap: '12px' }}>
                            <div style={{ width: '140px' }}>
                                <CustomSelect 
                                    options={monthOptions}
                                    value={monthOptions.find(opt => opt.value === selectedMonth)}
                                    onChange={(selected) => {
                                        setSelectedMonth(selected ? selected.value : new Date().getMonth() + 1);
                                        setPagination(prev => ({ ...prev, current_page: 1 }));
                                    }}
                                    placeholder="Month"
                                    isSearchable={false}
                                />
                            </div>
                            <div style={{ width: '120px' }}>
                                <CustomSelect 
                                    options={yearOptions}
                                    value={yearOptions.find(opt => opt.value === selectedYear)}
                                    onChange={(selected) => {
                                        setSelectedYear(selected ? selected.value : new Date().getFullYear());
                                        setPagination(prev => ({ ...prev, current_page: 1 }));
                                    }}
                                    placeholder="Year"
                                    isSearchable={false}
                                    onMenuScrollToBottom={handleYearScrollToBottom}
                                />
                            </div>
                        </div>
                    </div>

                    {summaryData && (
                        <div className={styles['summary-collapsible']}>
                            <button
                                className={styles['summary-toggle']}
                                onClick={() => setIsSummaryOpen(prev => !prev)}
                            >
                                <div className={styles['summary-toggle-left']}>
                                    <LayoutDashboard size={20} style={{ color: '#f97316' }} />
                                    <div className={styles['summary-toggle-text']}>
                                        <span className={styles['summary-toggle-title']}> Monthly Summary</span>
                                        <span className={styles['summary-toggle-hint']}>
                                            {summaryData.full_day} Full Days · {summaryData.absent} Absent
                                        </span>
                                    </div>
                                </div>
                                {isSummaryOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                            </button>
                            <div className={`${styles['metrics-grid']} ${isSummaryOpen ? styles['summary-expanded'] : styles['summary-collapsed']}`}>
                            <div className={styles['metric-card']}>
                                <div className={`${styles['metric-icon']} ${styles['success']}`}>
                                    <CheckCircle2 size={24} />
                                </div>
                                <div className={styles['metric-info']}>
                                    <span className={styles['metric-value']}>{summaryData.full_day}</span>
                                    <span className={styles['metric-label']}>Full Days Present</span>
                                </div>
                            </div>
                            <div className={styles['metric-card']}>
                                <div className={`${styles['metric-icon']} ${styles['warning']}`}>
                                    <AlertCircle size={24} />
                                </div>
                                <div className={styles['metric-info']}>
                                    <span className={styles['metric-value']}>{summaryData.half_day + summaryData.first_half + summaryData.second_half}</span>
                                    <span className={styles['metric-label']}>Half Days</span>
                                </div>
                            </div>
                            <div className={styles['metric-card']}>
                                <div className={`${styles['metric-icon']} ${styles['danger']}`}>
                                    <XCircle size={24} />
                                </div>
                                <div className={styles['metric-info']}>
                                    <span className={styles['metric-value']}>{summaryData.absent}</span>
                                    <span className={styles['metric-label']}>Absent</span>
                                </div>
                            </div>
                            <div className={styles['metric-card']}>
                                <div className={`${styles['metric-icon']} ${styles['purple']}`}>
                                    <Calendar size={24} />
                                </div>
                                <div className={styles['metric-info']}>
                                    <span className={styles['metric-value']}>{summaryData.leave}</span>
                                    <span className={styles['metric-label']}>On Leave</span>
                                </div>
                            </div>
                            </div>
                        </div>
                    )}

                    <div className={styles['data-view-container']}>
                        <div className={styles['table-wrapper']}>
                            <table className={styles['employee-table']}>
                                <thead>
                                    <tr>
                                        <th>DATE</th>
                                        <th>FIRST PUNCH IN</th>
                                        <th>LAST PUNCH OUT</th>
                                        <th>DURATION</th>
                                        <th>STATUS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {employeeData.length > 0 ? (
                                        employeeData.map((record) => (
                                            <tr key={record.id}>
                                                <td className={styles['date-cell']}>
                                                    {new Date(record.attendance_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                </td>
                                                <td className={styles['time-cell']}>
                                                    <div className={styles['punch-badge']}>
                                                        <Clock size={14} color="#16a34a" />
                                                        <span>{formatTime(record.first_punch_in)}</span>
                                                    </div>
                                                </td>
                                                <td className={styles['time-cell']}>
                                                    <div className={styles['punch-badge']}>
                                                        <Clock size={14} color="#dc2626" />
                                                        <span>{formatTime(record.last_punch_out)}</span>
                                                    </div>
                                                </td>
                                                <td className={styles['hours-cell']}>
                                                    {formatDuration(record.total_work_minutes)}
                                                </td>
                                                <td>
                                                    <span className={`${styles['status-pill']} ${getStatusClass(record.status)}`}>
                                                        <span className={styles['dot']}></span>
                                                        {record.status ? record.status.replace(/_/g, ' ') : '-'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    ) : !isLoading && (
                                        <NoData message="No attendance records found for this month." colSpan={5} isTable={true} />
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className={styles['attendance-cards']}>
                            {employeeData.length > 0 ? (
                                employeeData.map((record) => (
                                    <div key={record.id} className={styles['attendance-card']}>
                                        <div className={styles['card-header']}>
                                            <span className={styles['date-text']}>
                                                {new Date(record.attendance_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                            </span>
                                            <span className={`${styles['status-pill']} ${getStatusClass(record.status)}`}>
                                                <span className={styles['dot']}></span>
                                                {record.status ? record.status.replace(/_/g, ' ') : '-'}
                                            </span>
                                        </div>
                                        <div className={styles['card-body']}>
                                            <div className={styles['punch-info']}>
                                                <span className={styles['punch-label']}>In</span>
                                                <div className={styles['punch-badge']}>
                                                    <Clock size={14} color="#16a34a" />
                                                    <span>{formatTime(record.first_punch_in)}</span>
                                                </div>
                                            </div>
                                            <div className={styles['punch-info']}>
                                                <span className={styles['punch-label']}>Out</span>
                                                <div className={styles['punch-badge']}>
                                                    <Clock size={14} color="#dc2626" />
                                                    <span>{formatTime(record.last_punch_out)}</span>
                                                </div>
                                            </div>
                                            <div className={styles['punch-info']}>
                                                <span className={styles['punch-label']}>Duration</span>
                                                <span className={styles['hours-cell']}>
                                                    {formatDuration(record.total_work_minutes)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : !isLoading && (
                                <NoData message="No attendance records found for this month." isTable={false} />
                            )}
                        </div>

                        <div className={styles['desktop-pagination']}>
                            <Pagination 
                                currentPage={pagination.current_page}
                                totalItems={pagination.total}
                                itemsPerPage={pagination.per_page}
                                onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
                            />
                        </div>

                        <div className={styles['mobile-pagination']}>
                            <Pagination 
                                currentPage={pagination.current_page}
                                totalItems={pagination.total}
                                itemsPerPage={pagination.per_page}
                                onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
                            />
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default MonthlyReportView;
